import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Plus, Edit, Trash2, Save, X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface QuizQuestionsDialogProps {
  quiz: any
  isOpen: boolean
  onClose: () => void
}

interface Question {
  id?: string
  question: string
  options: string[]
  order_index: number
}

const QuizQuestionsDialog = ({ quiz, isOpen, onClose }: QuizQuestionsDialogProps) => {
  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null)
  const { toast } = useToast()

  const [newQuestion, setNewQuestion] = useState<Question>({
    question: '',
    options: ['A', 'B', 'C'],
    order_index: 1
  })

  useEffect(() => {
    if (quiz && isOpen) {
      fetchQuestions()
    }
  }, [quiz, isOpen])

  useEffect(() => {
    if (isOpen) {
      // Set up real-time subscription for questions
      const questionsChannel = supabase
        .channel('quiz-questions-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'quiz_questions'
          },
          () => {
            fetchQuestions()
          }
        )
        .subscribe()

      return () => {
        supabase.removeChannel(questionsChannel)
      }
    }
  }, [isOpen])

  const fetchQuestions = async () => {
    if (!quiz) return

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('quiz_questions')
        .select('*')
        .eq('quiz_id', quiz.id)
        .order('order_index')

      if (error) throw error
      setQuestions(data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching questions",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAddQuestion = async () => {
    if (!quiz || questions.length >= 3) {
      toast({
        title: "Cannot add more questions",
        description: "Each quiz can only have exactly 3 questions.",
        variant: "destructive",
      })
      return
    }

    if (!newQuestion.question.trim()) {
      toast({
        title: "Question text required",
        description: "Please enter a question.",
        variant: "destructive",
      })
      return
    }

    try {
      const questionData = {
        ...newQuestion,
        quiz_id: quiz.id,
        order_index: questions.length + 1
      }

      const { error } = await supabase
        .from('quiz_questions')
        .insert(questionData)

      if (error) throw error

      toast({ title: "Question added successfully" })
      setNewQuestion({
        question: '',
        options: ['A', 'B', 'C'],
        order_index: 1
      })
      fetchQuestions()
    } catch (error: any) {
      toast({
        title: "Error adding question",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleEditQuestion = async (question: Question) => {
    if (!question.id) return

    try {
      const { error } = await supabase
        .from('quiz_questions')
        .update({
          question: question.question,
          options: question.options
        })
        .eq('id', question.id)

      if (error) throw error

      toast({ title: "Question updated successfully" })
      setEditingQuestion(null)
      fetchQuestions()
    } catch (error: any) {
      toast({
        title: "Error updating question",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      const { error } = await supabase
        .from('quiz_questions')
        .delete()
        .eq('id', questionId)

      if (error) throw error

      toast({ title: "Question deleted successfully" })
      fetchQuestions()
    } catch (error: any) {
      toast({
        title: "Error deleting question",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleOptionChange = (questionIndex: number, optionIndex: number, value: string) => {
    if (editingQuestion) {
      const newOptions = [...editingQuestion.options]
      newOptions[optionIndex] = value
      setEditingQuestion({ ...editingQuestion, options: newOptions })
    }
  }

  if (!quiz) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quiz Questions for {quiz.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add New Question */}
          {questions.length < 3 && (
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-semibold">Add New Question ({questions.length + 1}/3)</h3>
              <p className="text-sm text-muted-foreground">
                Each quiz must have exactly 3 questions with A, B, C options. This is a personality/learning style quiz, not a knowledge test.
              </p>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="question">Question Text</Label>
                  <Textarea
                    id="question"
                    value={newQuestion.question}
                    onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                    placeholder="Enter your question here..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Options</Label>
                  <div className="grid grid-cols-3 gap-4">
                    {newQuestion.options.map((option, index) => (
                      <div key={index} className="space-y-2">
                        <Label className="text-sm font-medium">Option {String.fromCharCode(65 + index)}</Label>
                        <Input
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...newQuestion.options]
                            newOptions[index] = e.target.value
                            setNewQuestion({ ...newQuestion, options: newOptions })
                          }}
                          placeholder={`Option ${String.fromCharCode(65 + index)}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={handleAddQuestion} disabled={!newQuestion.question.trim()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </Button>
              </div>
            </div>
          )}

          {/* Existing Questions */}
          <div className="space-y-4">
            <h3 className="font-semibold">Existing Questions ({questions.length}/3)</h3>
            {loading ? (
              <p>Loading questions...</p>
            ) : (
              <div className="space-y-4">
                {questions.map((question, index) => (
                  <div key={question.id} className="border rounded-lg p-4">
                    {editingQuestion?.id === question.id ? (
                      // Edit Mode
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Question {index + 1}</h4>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleEditQuestion(editingQuestion)}
                            >
                              <Save className="w-4 h-4 mr-2" />
                              Save
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingQuestion(null)}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Question Text</Label>
                          <Textarea
                            value={editingQuestion.question}
                            onChange={(e) => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                            rows={3}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Options</Label>
                          <div className="grid grid-cols-3 gap-4">
                            {editingQuestion.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="space-y-2">
                                <Label className="text-sm font-medium">Option {String.fromCharCode(65 + optionIndex)}</Label>
                                <Input
                                  value={option}
                                  onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                                  placeholder={`Option ${String.fromCharCode(65 + optionIndex)}`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Question {index + 1}</h4>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingQuestion(question)}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteQuestion(question.id!)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </Button>
                          </div>
                        </div>
                        
                        <p className="text-gray-700">{question.question}</p>
                        
                        <div className="grid grid-cols-3 gap-4">
                          {question.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="flex items-center space-x-2">
                              <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                                {String.fromCharCode(65 + optionIndex)}
                              </Badge>
                              <span className="text-sm">{option}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {questions.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No questions configured yet. Add 3 questions to complete this quiz.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default QuizQuestionsDialog
